import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import CustomerApiService from "../../../CustomerApiService";

/**
 * Spotter File Rename tab API calls (Monday 12610379991, `PLAN-spotter-file-rename-v3.md`).
 * Mirrors `../gamebook/service.ts`'s convention (the newer, preferred shape per
 * `apps/customer-frontend/CLAUDE.md`'s "Data fetching" section): thin wrappers over
 * `CustomerApiService.fetch`, bare-JSON contract, plus the actual `useQuery`/`useMutation` hooks
 * exported directly rather than called inline in the screen.
 *
 * Types here mirror (not import — the two apps don't share a types package, see the house
 * convention doc) `apps/customer-api/src/services/customerSpotterRename.ts`'s `SpotterRenameCategory`/
 * `SpotterRenameSettings`.
 */

const PREFIX = "/spotter";

/** One per-category filename template row — see `customerSpotterRename.ts`'s doc comment for the
 *  full field-level rationale (categories are customer-extensible, not a fixed 3-item set). */
export interface RenameCategory {
  label: string;
  template: string;
}

/** Which of the org's two CineSys-configured Spotter views a `{Token}` names — mirrors
 *  `apps/customer-api/src/services/customerSpotterRename.ts`'s `SpotterRenameFieldScope`. */
export type RenameFieldScope = "asset" | "segment";

/** One `{Token}`'s resolved field identity. Mirrors `customerSpotterRename.ts`'s
 *  `SpotterRenameFieldToken` — see that file's doc comment for the full `view_id` story (Monday
 *  12624047544 item 2.4 for `field_id`/`scope`; Monday 12645331451 for `view_id`). Sent here
 *  populated (`RenameScreen.tsx`'s `computeFieldTokens` has the view's `id` on hand), but only as
 *  a shape placeholder — the server always re-derives it from `scope` + org config, never trusts
 *  this field as submitted (see `rename.ts`'s `reconcileFieldTokenViewIds`). */
export interface RenameFieldToken {
  field_id: string;
  scope: RenameFieldScope;
  view_id: string;
}

/** The org's persisted File Rename settings — `null` when nothing has ever been saved for this
 *  org (see `apps/customer-api/src/routes/spotter/rename.ts`'s doc comment for why this is a
 *  DIFFERENT sentinel than Mapping/Glossary's "empty array" convention: `RenameScreen.tsx` needs
 *  to tell "nothing saved yet" apart from "saved, but every category was deliberately removed"). */
export interface RenameSettings {
  categories: RenameCategory[];
  replaceAssetTitle: boolean;
  includeFileExtensionInTitle: boolean;
  /** Same `{field_id, scope, view_id}` shape as `fieldTokens`' values (Monday 12645331451,
   *  follow-up round — see `customerSpotterRename.ts`'s doc comment on
   *  `SpotterRenameSettings.originalNameField` for why its `view_id` is validated server-side
   *  rather than re-derived, unlike `fieldTokens.view_id`). `""` means unset. */
  originalNameField: RenameFieldToken | "";
  useMetadataValues: boolean;
  /** Token identity map for every real-field `{Token}` (see `RenameFieldToken`'s doc comment).
   *  Org-wide, not per-category — `RenameScreen.tsx`'s `computeFieldTokens` recomputes this from
   *  the live `GET /iconik-views` field catalog and resubmits it in full on every save. Keyed by
   *  the token's literal `{...}` text, which is USUALLY the bare field name but gets a `Timed:`
   *  prefix for a segment-scope field whose name collides with an asset-scope one — see
   *  `RenameScreen.tsx`'s "TOKEN-TEXT COLLISIONS" doc-comment section and `./tokenIdentity.ts`. */
  fieldTokens: Record<string, RenameFieldToken>;
}

/** `GET /rename` response envelope. `categoryOptions` is CineSys-configured per-org plugin
 *  variable data (`renameCategoryOptions`, `apps/backend/src/definitions/plugins/spotter.ts`) — read-only here, NOT
 *  part of `settings`, and present even when `settings` is `null` (nothing saved yet). */
export interface RenameSettingsResponse {
  settings: RenameSettings | null;
  categoryOptions: string[];
}

/** `PUT /rename` response envelope — same shape as `RenameSettingsResponse`, except `settings` is
 *  never `null` (a PUT always echoes back the just-saved, server-sanitized settings). */
export interface SaveRenameSettingsResponse {
  settings: RenameSettings;
  categoryOptions: string[];
}

/** One field within a customer's iconik metadata view. Mirrors `../mapping/service.ts`'s
 *  `IconikMetadataField` (not imported — see the house "mirror don't share types" convention),
 *  EXTENDED with `options` (picklist values) — `../mapping/service.ts` doesn't need it (Mapping
 *  never previews a resolved value), but this file's "Resolves to" preview does (item 2.6): a
 *  `drop_down`/`tag_cloud` field's first option is used as its illustrative sample. Confirmed
 *  present at the wire level already — `iconik-manager's packages/iconik/src/iconikMetadata.ts`'s own
 *  `IconikMetadataField` (what `GET /iconik-views` actually serializes) has always carried
 *  `options?: {label, value}[]`; this frontend mirror just hadn't declared it before now (see
 *  `apps/customer-api/src/routes/spotter/glossary.ts`'s doc comment for the live-confirmed
 *  drop_down/tag_cloud `options` round-trip). No backend change needed. */
export interface IconikMetadataField {
  name: string;
  label: string;
  field_type: string;
  options?: { label: string; value: string }[];
}

/** `GET /iconik-views/rename` response entry. Mirrors `../mapping/service.ts`'s `IconikMetadataView`
 *  shape (`id`/`scope` included, not just `view_fields`) — but NOT its picker UX: the Original
 *  Name Field picker is a single flattened `Select` grouped by view name, not the two-step
 *  "choose a view, then a field within it" pattern `SpotterViewPicker`/`FieldsScreen.tsx` use
 *  (see `RenameScreen.tsx`'s "ORIGINAL NAME FIELD" doc-comment section for the full story). The
 *  `id`/`scope` fields are still needed here — they feed `viewKey`/`fieldOptionValue`'s composite
 *  option values, which disambiguate a same-named field appearing in more than one view. */
export interface IconikMetadataView {
  id: string;
  name: string;
  description: string;
  view_fields: IconikMetadataField[];
  scope: "asset" | "segment";
}

/** GET `/rename` — the org's existing saved File Rename settings (or `null` if none yet) plus the
 *  CineSys-configured `categoryOptions` list. */
export async function getRenameSettings(): Promise<RenameSettingsResponse> {
  return CustomerApiService.fetch<RenameSettingsResponse>(
    "GET",
    `${PREFIX}/rename`
  );
}

/** PUT `/rename` — persist the full current settings. Returns the (server-sanitized) saved shape
 *  plus `categoryOptions`. */
export async function saveRenameSettings(
  settings: RenameSettings
): Promise<SaveRenameSettingsResponse> {
  return CustomerApiService.fetch<SaveRenameSettingsResponse>(
    "PUT",
    `${PREFIX}/rename`,
    settings
  );
}

/** GET `/iconik-views/rename` — the Original Name Field picker's view+field source. A DIFFERENT
 *  endpoint than the Field Mapping tab's destination-field picker (`../mapping/service.ts`'s
 *  `getIconikViews`, `GET /iconik-views`): that one is scoped to the org's two CineSys-configured
 *  Field Mapping views, while this one offers every view in any of the org's metadata-view
 *  categories — Rename's source field isn't limited to those two views. See
 *  `apps/customer-api/src/routes/spotter/iconikViews.ts`'s doc comment. */
export async function getRenameIconikViews(): Promise<IconikMetadataView[]> {
  return CustomerApiService.fetch<IconikMetadataView[]>(
    "GET",
    `${PREFIX}/iconik-views/rename`
  );
}

/** GET `/iconik-views` — the `{Token}` chip / field-identity source (`RenameScreen.tsx`'s
 *  `assetView`/`segmentView`/`flatFieldEntries`), kept on the SAME two-CineSys-configured-view
 *  endpoint Field Mapping uses. Deliberately a SEPARATE query from `useRenameIconikViews` above:
 *  the "REAL TOKENS" design (file doc comment's Monday 12624047544 section) assumes at most one
 *  view per scope, which only holds for the two-view-scoped response — the Original Name Field
 *  picker's own source was reverted back to the unfiltered, possibly-many-views-per-scope
 *  category listing, which that assumption can't survive. */
export async function getRenameTokenViews(): Promise<IconikMetadataView[]> {
  return CustomerApiService.fetch<IconikMetadataView[]>(
    "GET",
    `${PREFIX}/iconik-views`
  );
}

export const renameQueryKeys = {
  settings: ["spotter-rename-settings"] as const,
  iconikViews: ["spotter-rename-iconik-views"] as const,
  tokenViews: ["spotter-rename-token-views"] as const,
};

export function useRenameSettings(): UseQueryResult<
  RenameSettingsResponse,
  Error
> {
  return useQuery<RenameSettingsResponse, Error>(
    renameQueryKeys.settings,
    getRenameSettings
  );
}

/** `staleTime` matches `useGamebookConfig`'s reasoning — the org's own metadata-field catalog
 *  doesn't change from one render to the next. */
export function useRenameIconikViews(): UseQueryResult<
  IconikMetadataView[],
  Error
> {
  return useQuery<IconikMetadataView[], Error>(
    renameQueryKeys.iconikViews,
    getRenameIconikViews,
    { staleTime: 60_000 }
  );
}

/** `staleTime` matches `useRenameIconikViews`'s reasoning above. */
export function useRenameTokenViews(): UseQueryResult<
  IconikMetadataView[],
  Error
> {
  return useQuery<IconikMetadataView[], Error>(
    renameQueryKeys.tokenViews,
    getRenameTokenViews,
    { staleTime: 60_000 }
  );
}

export function useSaveRenameSettings() {
  const queryClient = useQueryClient();
  return useMutation(saveRenameSettings, {
    onSuccess: (data) => {
      queryClient.setQueryData(renameQueryKeys.settings, data);
    },
  });
}
